import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const AVATAR_URL = "/images/logo-avatar.png";
const CTA_URL = "https://diyb.ca/contact-diyb/";

const navLinks = [
  { label: "The Shift", href: "#shift" },
  { label: "What I Build", href: "#services" },
  { label: "The Code", href: "#philosophy" },
  { label: "Proof", href: "#proof" },
  { label: "How It Works", href: "#how-it-works" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-navy-500/80 backdrop-blur-xl border-b border-slate-800/50 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="#hero" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={AVATAR_URL}
            alt="White Beard Solutions"
            className="w-10 h-10 rounded-full border-2 border-gold/40 group-hover:border-gold transition-colors duration-300"
          />
          <span className="font-heading font-bold text-xl tracking-wide text-slate-50 hidden sm:block">
            WHITE BEARD <span className="text-gold">SOLUTIONS</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="link-underline font-body text-[1.1rem] font-semibold tracking-wide text-white hover:text-gold transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
          <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="nav-cta-button"
              className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-sm px-6 py-2 rounded-sm transition-all duration-300 hover:shadow-[0_0_20px_-5px_rgba(212,175,55,0.5)]"
            >
              Start Your Build
            </Button>
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-slate-300 hover:text-gold transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-navy-500/95 backdrop-blur-xl border-t border-slate-800/50"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-body text-[1.1rem] font-semibold text-white hover:text-gold transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="mobile-cta-button"
                  className="w-full bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase mt-2 rounded-sm"
                >
                  Start Your Build
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
